import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { RefreshCw, WifiOff, X } from 'lucide-react';
import { Button } from '@/components/ui';
import { safeInternalPath } from '@/lib/safe-path';
import { applyUpdate, usePwa } from '@/lib/pwa';

/**
 * Globale PWA-Hinweise: „Neue Version verfügbar“, Offline-Hinweis und Navigation aus
 * Push-Benachrichtigungen (Service Worker sendet { type: 'NAVIGATE', url }).
 */
export function PwaStatus() {
  const { updateAvailable } = usePwa();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState(false);
  const [online, setOnline] = useState(() => (typeof navigator === 'undefined' ? true : navigator.onLine));

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const h = (e: MessageEvent) => {
      const d = e.data as { type?: string; url?: string } | null;
      const path = d?.type === 'NAVIGATE' ? safeInternalPath(d.url) : null;
      if (path) navigate(path);
    };
    navigator.serviceWorker.addEventListener('message', h);
    return () => navigator.serviceWorker.removeEventListener('message', h);
  }, [navigate]);

  return (
    <>
      {!online && (
        <div className="fixed inset-x-0 top-0 z-[70] flex items-center justify-center gap-2 bg-slate-800 px-3 py-1.5 text-xs text-white" role="status">
          <WifiOff className="h-3.5 w-3.5" /> Keine Internetverbindung – Daten können derzeit nicht geladen oder gespeichert werden.
        </div>
      )}
      {updateAvailable && !dismissed && (
        <div
          className="fixed bottom-4 left-4 z-[60] flex w-[calc(100%-2rem)] max-w-sm items-center gap-3 rounded-lg border border-brand-200 bg-white p-3 text-sm shadow-lg"
          role="status"
        >
          <RefreshCw className="h-5 w-5 shrink-0 text-brand-700" />
          <p className="flex-1 text-slate-700">Neue Version verfügbar</p>
          <Button size="sm" variant="primary" onClick={applyUpdate}>
            Neu laden
          </Button>
          <button type="button" className="text-slate-400 hover:text-slate-600" onClick={() => setDismissed(true)} aria-label="Später">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </>
  );
}
